import { MedplumClient, QueryTypes, ResourceArray } from "@medplum/core";
import { Bundle, CareTeam, Practitioner } from "@medplum/fhirtypes";

import { getResourcesFromBundle, joinReferenceString } from "@/utils/medplum";

export interface CareTeamWithIncludedPractitionersResult {
  careTeams: CareTeam[];
  practitioners: Practitioner[];
}

/**
 * Searches CareTeam resources with `_include` and extracts CareTeam + Practitioner
 * resources from the response bundle (included practitioners are not in the unwrapped array).
 *
 * @param medplum - Authenticated Medplum client.
 * @param query - CareTeam search params; must include `_include` for participant practitioners.
 * @returns Matching care teams and included practitioners from the bundle.
 */
export async function searchCareTeamsWithIncludedPractitioners(
  medplum: MedplumClient,
  query: QueryTypes,
): Promise<CareTeamWithIncludedPractitionersResult> {
  const response = (await medplum.searchResources("CareTeam", query, {
    cache: "reload",
  })) as ResourceArray<CareTeam>;

  const bundle = response.bundle as Bundle;

  return {
    careTeams: getResourcesFromBundle<CareTeam>("CareTeam", bundle as Bundle<CareTeam>),
    practitioners: getResourcesFromBundle<Practitioner>(
      "Practitioner",
      bundle as Bundle<Practitioner>,
    ),
  };
}

/**
 * Fetches practitioners on a patient's active care teams (for signature pickers, etc.).
 *
 * @param medplum - Authenticated Medplum client.
 * @param patientId - Patient id (without `Patient/` prefix).
 * @returns Practitioners included from active care team searches.
 */
export async function fetchActiveCareTeamPractitionersForPatient(
  medplum: MedplumClient,
  patientId: string,
): Promise<Practitioner[]> {
  const { practitioners } = await searchCareTeamsWithIncludedPractitioners(medplum, {
    subject: joinReferenceString("Patient", patientId),
    status: "active",
    _include: "CareTeam:participant:Practitioner",
    _count: 1000,
  });

  return practitioners;
}
